import { XCircleIcon } from '@heroicons/react/24/outline';
import { Cog8ToothIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { getBaseRiskStatusOptions } from '@/components/legacy-ui/RiskDropdown';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { useAuth } from '@/state/auth';
import { Risk, RiskDeletedSubStatus, RiskStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Get status options for dropdown based on user type and mode
 * @param isPraetorianUser - Whether user is a Praetorian user
 * @param mode - The current vulnerability status mode
 * @returns Status options including Pending for Praetorian users
 */
const getStatusOptions = (
  isPraetorianUser: boolean,
  mode: 'legacy' | 'new'
) => [
  ...getBaseRiskStatusOptions(isPraetorianUser, mode),
  {
    label: getStatusLabel(RiskStatus.Deleted, mode),
    value: 'reject',
    icon: <XCircleIcon className="size-4" />,
    count: '⌥ R',
  },
];

// Close reason options
const closeReasons = [
  {
    label: 'False Positive',
    value: RiskDeletedSubStatus.FalsePositive,
    description:
      'This risk is deemed to be a false positive or not valid, and no further action will be taken.',
  },
  {
    label: 'Out of Scope',
    value: RiskDeletedSubStatus.OutOfScope,
    description:
      'This risk is out of scope and will not be addressed or tracked.',
  },
  {
    label: 'Duplicate',
    value: RiskDeletedSubStatus.Duplicate,
    description:
      'This risk is a duplicate of another risk and will not be addressed or tracked.',
  },
  {
    label: 'Other',
    value: RiskDeletedSubStatus.Other,
    description: 'There is something else that is not listed above.',
  },
];

export function ChangeRiskStatus({
  risks,
  onUpdateRisk,
  disabled = false,
}: {
  risks: Risk[];
  onUpdateRisk: (risk: Risk, comment?: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const { isPraetorianUser } = useAuth();
  const { mode } = useVulnerabilityStatusMode();

  // Get status options filtered by user type with mode-aware labels
  const statusOptions = getStatusOptions(isPraetorianUser, mode);

  const handleStatusChange = async (selectedValue: string) => {
    if (selectedValue === 'reject') {
      setIsCloseModalOpen(true);
      return;
    }

    try {
      await Promise.all(
        risks.map(async risk => {
          const currentSeverity = risk.status[1];
          const combinedStatus = `${selectedValue}${currentSeverity}`;
          const updatedRisk = { ...risk, status: combinedStatus };
          await onUpdateRisk(updatedRisk);
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Dropdown
        options={statusOptions}
        onSelect={option => handleStatusChange(option.value as string)}
        styleType="rounded"
        size="sm"
        icon={<Cog8ToothIcon className="size-4" />}
        placeholder="Change Status"
        disabled={disabled}
      />
      <CloseModal
        open={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        risks={risks}
        onUpdateRisk={onUpdateRisk}
      />
    </>
  );
}

export const CloseModal = ({
  open,
  onClose,
  risks,
  onUpdateRisk,
}: {
  open: boolean;
  onClose: () => void;
  risks: Risk[];
  onUpdateRisk: (risk: Risk, comment?: string) => Promise<void>;
}) => {
  const [selectedCloseReason, setSelectedCloseReason] = useState<string>('');

  const handleCloseConfirm = async () => {
    if (!selectedCloseReason) return;

    try {
      const closeReason = closeReasons.find(
        r => r.value === selectedCloseReason
      );

      await Promise.all(
        risks.map(async risk => {
          const updatedRisk = {
            ...risk,
            status: `${RiskStatus.Deleted}${risk.status[1]}${closeReason?.value}`,
          };
          await onUpdateRisk(updatedRisk);
        })
      );

      onClose();
      setSelectedCloseReason('');
    } catch {
      console.error('Failed to update risk status');
    }
  };

  return (
    <Modal
      title="Reject Risk"
      open={open}
      onClose={() => {
        onClose();
        setSelectedCloseReason('');
      }}
      footer={{
        text: 'Confirm',
        onClick: handleCloseConfirm,
        disabled: !selectedCloseReason,
      }}
      className="py-0"
    >
      <div className="space-y-4 py-2 pt-0">
        <hr className="border-chariot-stroke w-full" />
        <p className="text-chariot-text-secondary text-sm">
          {risks.length === 1
            ? 'Select a reason for closing this risk:'
            : `Select a reason for closing these ${risks.length} risks:`}
        </p>
        <div className="space-y-2">
          {closeReasons.map((reason, key) => (
            <Radio
              key={key}
              selected={selectedCloseReason}
              label={reason.label}
              description={reason.description}
              value={reason.value}
              onChange={setSelectedCloseReason}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

const Radio = ({
  selected,
  label,
  description,
  value,
  onChange,
}: {
  selected: string;
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const checked = selected === value;
  return (
    <label
      className={cn(
        'border-chariot-stroke hover:bg-chariot-background-secondary flex items-start gap-3 rounded-md border p-4',
        checked && 'bg-chariot-background-secondary'
      )}
    >
      <input
        type="radio"
        name="deletedStatus"
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-chariot-text-secondary text-sm">{description}</div>
      </div>
    </label>
  );
};
